'use client';

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { useAuth } from '@/app/auth-provider';
import { reloadCurrentProjectPage } from '@/lib/project-navigation';
import { confirmProjectSwitch } from '@/lib/project-switch-guard';
import { getPersistedCurrentProjectId, persistCurrentProjectId } from '@/lib/project-selection';
import { group, resetGroups } from '@/lib/posthog';
import type {
  AnalysisSettings,
  BrandMergeGroup,
  BrandSettings,
  ProjectFeatureSettings,
  PromptGroup,
} from '@/types/database-schema';
import type {
  InitialProjectState,
  SerializedManageableWorkspace,
  SerializedProject,
  WorkspaceManagementBootstrap,
} from '@/types/projects';

export type Project = SerializedProject;
export type ManageableWorkspace = SerializedManageableWorkspace;

/**
 * Project creation input
 */
export interface CreateProjectInput {
  idempotency_key?: string;
  workspace_id?: string;
  name: string;
  description?: string;
  brand_settings: {
    brand_name: string;
    website_url: string;
    brand_variations?: string[];
    company_description?: string;
    industry?: string;
    value_proposition?: string;
    target_audience?: string;
    key_use_cases?: string[];
    top_competitors?: string[];
  };
  brand_merge_groups?: BrandMergeGroup[];
  analysis_settings?: AnalysisSettings;
}

export interface CreateOnboardingProjectShellInput {
  workspace_id?: string;
  name?: string;
  website_url?: string;
  idempotency_key?: string;
}

/**
 * Project update input
 */
export interface UpdateProjectInput {
  name?: string;
  description?: string;
  brand_settings?: Partial<BrandSettings>;
  brand_merge_groups?: BrandMergeGroup[];
  analysis_settings?: Partial<AnalysisSettings>;
  prompt_groups?: PromptGroup[];
  prompt_topics_revision?: string;
  prompt_topics_sql?: boolean;
  feature_settings?: ProjectFeatureSettings;
  status?: 'active' | 'archived';
  onboarding_completed?: boolean;
  onboarding_job_id?: string;
}

/**
 * Migration status
 */
export interface MigrationStatus {
  status: 'not_needed' | 'pending' | 'completed' | 'partial';
  has_brand_settings: boolean;
  has_projects: boolean;
  project_count: number;
  unlinked_jobs_count: number;
  default_project_id: string | null;
}

interface ProjectContextType {
  // Current project state
  currentProject: Project | null;
  currentProjectId: string | null;
  projects: Project[];
  manageableWorkspaces: ManageableWorkspace[];
  canCreatePersonalProject: boolean;
  loading: boolean;
  projectsHydrated: boolean;
  error: string | null;

  // Ready state - true only after initial project ID is settled
  // Hooks should wait for this before making queries to avoid abort/retry cycles
  isReady: boolean;

  // Transition state - prevents data "blinks" during project switches
  isTransitioning: boolean;
  previousProjectId: string | null;

  // Project operations
  fetchProjects: (force?: boolean, options?: { clearProjectSelection?: boolean }) => Promise<void>;
  switchProject: (projectId: string, options?: { preserveRoute?: boolean }) => void;
  createProject: (input: CreateProjectInput) => Promise<Project>;
  createOnboardingProjectShell: (input?: CreateOnboardingProjectShellInput) => Promise<Project>;
  updateProject: (projectId: string, input: UpdateProjectInput) => Promise<void>;
  completeProjectSetup: (projectId: string, status: 'completed' | 'skipped') => Promise<void>;
  deleteProject: (
    projectId: string,
    force?: boolean,
    options?: { selectFallback?: boolean }
  ) => Promise<void>;

  // Migration helpers
  checkMigrationStatus: () => Promise<MigrationStatus | null>;
  migrateToProject: () => Promise<{ success: boolean; project_id?: string }>;
}

const ProjectContext = createContext<ProjectContextType>({
  currentProject: null,
  currentProjectId: null,
  projects: [],
  manageableWorkspaces: [],
  canCreatePersonalProject: false,
  loading: true,
  projectsHydrated: false,
  error: null,
  isReady: false,
  isTransitioning: false,
  previousProjectId: null,
  fetchProjects: async () => {},
  switchProject: () => {},
  createProject: async () => {
    throw new Error('ProjectProvider not initialized');
  },
  createOnboardingProjectShell: async () => {
    throw new Error('ProjectProvider not initialized');
  },
  updateProject: async () => {},
  completeProjectSetup: async () => {},
  deleteProject: async () => {},
  checkMigrationStatus: async () => null,
  migrateToProject: async () => ({ success: false }),
});

export const useProject = () => useContext(ProjectContext);

// Optional read status must not invalidate every project consumer in the app.
const WorkspaceManagementStatusContext = createContext<{
  manageableWorkspacesLoading: boolean;
  manageableWorkspacesError: string | null;
}>({ manageableWorkspacesLoading: false, manageableWorkspacesError: null });
export const useWorkspaceManagementStatus = () => useContext(WorkspaceManagementStatusContext);

const WorkspaceManagementSeedContext = createContext<
  (result: WorkspaceManagementBootstrap) => void
>(() => {});

/** Apply streamed optional data without suspending or remounting the project provider. */
export function WorkspaceManagementSeed({ result }: { result: WorkspaceManagementBootstrap }) {
  const apply = useContext(WorkspaceManagementSeedContext);
  useEffect(() => apply(result), [apply, result]);
  return null;
}

// Cache projects per-user to avoid blocking UI on cold starts / slow API responses.
// IMPORTANT: This is UI-only cache; API remains the source of truth.
const PROJECTS_CACHE_PREFIX = 'geobot_projects_cache_v2:';
const PROJECTS_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const EMPTY_PROJECTS: Project[] = [];
const EMPTY_MANAGEABLE_WORKSPACES: ManageableWorkspace[] = [];

type ProjectsCachePayload = {
  fetchedAt: number;
  projects: Project[];
  manageableWorkspaces: ManageableWorkspace[];
  canCreatePersonalProject: boolean;
};

function selectValidProjectId(
  projects: Project[],
  storedProjectId: string | null,
  defaultProjectId: string | null | undefined
): string | null {
  if (storedProjectId && projects.some(p => p.id === storedProjectId)) return storedProjectId;
  if (defaultProjectId && projects.some(p => p.id === defaultProjectId)) return defaultProjectId;
  return projects[0]?.id || null;
}

export function ProjectProvider({
  children,
  allowSessionCookieAuth = false,
  initialProjectState = null,
}: {
  children: React.ReactNode;
  allowSessionCookieAuth?: boolean;
  initialProjectState?: InitialProjectState | null;
}) {
  const { user, userDocument, loading: authLoading } = useAuth();
  const seededOwnerKey = initialProjectState?.ownerKey ?? null;

  const [projects, setProjects] = useState<Project[]>(() => initialProjectState?.projects ?? []);
  const [manageableWorkspaces, setManageableWorkspaces] = useState<ManageableWorkspace[]>(
    () => initialProjectState?.manageableWorkspaces ?? []
  );
  const [managementPending, setManagementPending] = useState(
    Boolean(initialProjectState?.managementPending)
  );
  const [managementError, setManagementError] = useState<string | null>(null);
  const [canCreatePersonalProject, setCanCreatePersonalProject] = useState(
    () => initialProjectState?.canCreatePersonalProject ?? false
  );
  const [hydratedOwnerKey, setHydratedOwnerKey] = useState<string | null>(seededOwnerKey);
  const [projectsOwnerKey, setProjectsOwnerKey] = useState<string | null>(seededOwnerKey);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(
    () => initialProjectState?.currentProjectId ?? null
  );
  const [loading, setLoading] = useState(!initialProjectState);
  const [error, setError] = useState<string | null>(null);

  // Ready state - only true after initial API fetch completes (project ID settled)
  // This prevents hooks from making queries during the cache→API→reconciliation cascade
  const [isReady, setIsReady] = useState(Boolean(initialProjectState));
  const hasInitialFetchRef = useRef(Boolean(initialProjectState));

  // Transition tracking - prevents data "blinks" during project switches
  const [isTransitioning, setIsTransitioning] = useState(false);
  const previousProjectIdRef = useRef<string | null>(null);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const projectsRef = useRef<Project[]>(initialProjectState?.projects ?? []);
  const projectsOwnerKeyRef = useRef<string | null>(seededOwnerKey);
  const activeOwnerKeyRef = useRef<string | null>(seededOwnerKey);
  const defaultProjectIdRef = useRef<string | null>(null);
  const skipNextAuthenticatedFetchRef = useRef(Boolean(initialProjectState));
  const skipSeededSessionFetchRef = useRef(Boolean(initialProjectState));
  const activeFetchVersionRef = useRef(0);

  useEffect(() => {
    projectsRef.current = projects;
  }, [projects]);

  useEffect(() => {
    projectsOwnerKeyRef.current = projectsOwnerKey;
  }, [projectsOwnerKey]);

  useEffect(() => {
    defaultProjectIdRef.current = userDocument?.default_project_id ?? null;
  }, [userDocument?.default_project_id]);

  const activeOwnerKey =
    user?.uid ?? (allowSessionCookieAuth && authLoading ? seededOwnerKey : null);
  const projectsBelongToActiveOwner = projectsOwnerKey === activeOwnerKey;
  const isOwnerTransition = Boolean(activeOwnerKey) && projectsOwnerKey !== activeOwnerKey;
  const activeProjects = useMemo(
    () => (projectsBelongToActiveOwner ? projects : EMPTY_PROJECTS),
    [projects, projectsBelongToActiveOwner]
  );
  const activeManageableWorkspaces = projectsBelongToActiveOwner
    ? manageableWorkspaces
    : EMPTY_MANAGEABLE_WORKSPACES;
  const activeCanCreatePersonalProject = projectsBelongToActiveOwner
    ? canCreatePersonalProject
    : false;
  const activeCurrentProjectId = projectsBelongToActiveOwner ? currentProjectId : null;
  const applyWorkspaceManagement = useCallback(
    (result: WorkspaceManagementBootstrap) => {
      if (
        result.ownerKey !== activeOwnerKey ||
        result.ownerKey !== projectsOwnerKeyRef.current ||
        result.fetchedAt !== initialProjectState?.fetchedAt ||
        activeFetchVersionRef.current !== 0
      )
        return;
      setManageableWorkspaces(current =>
        current.length === 0 && result.manageableWorkspaces.length === 0
          ? current
          : result.manageableWorkspaces
      );
      setManagementError(result.error);
      setManagementPending(false);
    },
    [activeOwnerKey, initialProjectState?.fetchedAt]
  );

  useEffect(() => {
    if (authLoading && !allowSessionCookieAuth) return;
    if (activeOwnerKeyRef.current === activeOwnerKey) return;

    activeOwnerKeyRef.current = activeOwnerKey;
    setHydratedOwnerKey(null);
    activeFetchVersionRef.current += 1;
    setError(null);
    setManagementPending(false);
    setManagementError(null);

    if (!activeOwnerKey) {
      hasInitialFetchRef.current = false;
      setIsReady(false);
      setProjects([]);
      setManageableWorkspaces([]);
      setCanCreatePersonalProject(false);
      setProjectsOwnerKey(null);
      setCurrentProjectId(null);
      setLoading(false);
      hasInitialFetchRef.current = true;
      setIsReady(true);
      return;
    }

    const canAdoptSeededProjects =
      allowSessionCookieAuth &&
      Boolean(user) &&
      projectsOwnerKeyRef.current === user?.uid &&
      skipNextAuthenticatedFetchRef.current;

    if (!canAdoptSeededProjects) {
      hasInitialFetchRef.current = false;
      setIsReady(false);
    }

    if (!canAdoptSeededProjects && projectsOwnerKeyRef.current !== activeOwnerKey) {
      setProjects([]);
      setManageableWorkspaces([]);
      setCanCreatePersonalProject(false);
      setProjectsOwnerKey(null);
      setCurrentProjectId(null);
    }

    setLoading(!canAdoptSeededProjects);
  }, [activeOwnerKey, allowSessionCookieAuth, authLoading, user]);

  // Compute current project from active user's projects list
  const currentProject = useMemo(() => {
    if (!activeCurrentProjectId) return null;
    return activeProjects.find(p => p.id === activeCurrentProjectId) || null;
  }, [activeProjects, activeCurrentProjectId]);

  useLayoutEffect(() => {
    if (!currentProject) {
      resetGroups();
      return;
    }
    group('project', currentProject.id, {
      name: currentProject.name || currentProject.brand_settings?.brand_name || currentProject.id,
      workspace_id: currentProject.workspace_id || null,
    });
  }, [currentProject]);

  // Fetch all projects for the user
  const fetchProjects = useCallback(
    async (force = false, options?: { clearProjectSelection?: boolean }) => {
      if (authLoading && !allowSessionCookieAuth) {
        return;
      }

      const isSeededSessionBootstrap =
        allowSessionCookieAuth && authLoading && !user && Boolean(seededOwnerKey);

      if (!user && !isSeededSessionBootstrap) {
        activeFetchVersionRef.current += 1;
        setProjects([]);
        setManageableWorkspaces([]);
        setCanCreatePersonalProject(false);
        setProjectsOwnerKey(null);
        setCurrentProjectId(null);
        setLoading(false);
        if (!hasInitialFetchRef.current) {
          hasInitialFetchRef.current = true;
          setIsReady(true);
        }
        return;
      }

      const requestOwnerKey = user?.uid ?? seededOwnerKey;
      if (!requestOwnerKey || activeOwnerKeyRef.current !== requestOwnerKey) {
        return;
      }

      if (
        !force &&
        skipSeededSessionFetchRef.current &&
        requestOwnerKey &&
        hasInitialFetchRef.current &&
        projectsOwnerKeyRef.current === requestOwnerKey
      ) {
        skipSeededSessionFetchRef.current = false;
        setLoading(false);
        setIsReady(true);
        return;
      }

      if (
        !force &&
        allowSessionCookieAuth &&
        !authLoading &&
        user &&
        skipNextAuthenticatedFetchRef.current
      ) {
        skipNextAuthenticatedFetchRef.current = false;
        if (projectsOwnerKeyRef.current === user.uid) {
          setLoading(false);
          hasInitialFetchRef.current = true;
          setIsReady(true);
          return;
        }
      }

      const fetchVersion = ++activeFetchVersionRef.current;
      setManagementPending(true);
      setManagementError(null);
      const isLatestFetch = () =>
        activeFetchVersionRef.current === fetchVersion &&
        activeOwnerKeyRef.current === requestOwnerKey;

      // Avoid "blocking" UI if we already have something to show (cached or previously loaded).
      const hadExistingProjects =
        Boolean(requestOwnerKey) &&
        projectsOwnerKeyRef.current === requestOwnerKey &&
        projectsRef.current.length > 0;
      if (!hadExistingProjects) {
        setLoading(true);
      }
      setError(null);

      try {
        const headers: HeadersInit = {};
        if (user) {
          const token = await user.getIdToken();
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch('/api/projects?include_archived=false', {
          headers,
        });

        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }

        const data = await response.json();
        if (!isLatestFetch()) return;

        const nextProjects: Project[] = Array.isArray(data.projects) ? data.projects : [];
        const nextManageableWorkspaces: ManageableWorkspace[] = Array.isArray(
          data.manageable_workspaces
        )
          ? data.manageable_workspaces
          : [];
        const nextCanCreatePersonalProject = Boolean(data.can_create_personal_project);
        setProjects(nextProjects);
        setHydratedOwnerKey(requestOwnerKey);
        setManageableWorkspaces(nextManageableWorkspaces);
        setManagementPending(false);
        setCanCreatePersonalProject(nextCanCreatePersonalProject);
        setProjectsOwnerKey(requestOwnerKey);

        if (isSeededSessionBootstrap) {
          skipNextAuthenticatedFetchRef.current = true;
        }

        // Cache projects for faster initial render on subsequent visits.
        if (user) {
          try {
            const cacheKey = `${PROJECTS_CACHE_PREFIX}${user.uid}`;
            const payload: ProjectsCachePayload = {
              fetchedAt: Date.now(),
              projects: nextProjects,
              manageableWorkspaces: nextManageableWorkspaces,
              canCreatePersonalProject: nextCanCreatePersonalProject,
            };
            localStorage.setItem(cacheKey, JSON.stringify(payload));
          } catch {
            // Non-blocking: localStorage may be unavailable (private mode, quota, etc.)
          }
        }

        // Determine current project
        const storedProjectId = getPersistedCurrentProjectId();
        const defaultProjectId = defaultProjectIdRef.current;

        // Priority: stored > default > first active project
        const validProjectId = options?.clearProjectSelection
          ? null
          : selectValidProjectId(nextProjects, storedProjectId, defaultProjectId);

        setCurrentProjectId(validProjectId);
        persistCurrentProjectId(validProjectId);
      } catch (err) {
        if (!isLatestFetch()) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch projects');
        setManagementError('Workspace options could not load.');
        // If we already have cached/previous projects, keep them to avoid UI regressions on transient errors.
        if (projectsRef.current.length === 0) {
          setProjects([]);
          setManageableWorkspaces([]);
          setCanCreatePersonalProject(false);
          setProjectsOwnerKey(requestOwnerKey);
        }
      } finally {
        if (!isLatestFetch()) return;
        setManagementPending(false);
        setLoading(false);
        // Mark as ready after the first API fetch completes (project ID is now settled)
        if (!hasInitialFetchRef.current) {
          hasInitialFetchRef.current = true;
          setIsReady(true);
        }
      }
    },
    [allowSessionCookieAuth, authLoading, seededOwnerKey, user]
  );

  // Switch to a different project with transition tracking
  const switchProject = useCallback(
    (projectId: string, options?: { preserveRoute?: boolean }) => {
      const nextProject = activeProjects.find(project => project.id === projectId);
      if (nextProject && projectId !== activeCurrentProjectId) {
        if (!options?.preserveRoute && typeof window !== 'undefined') {
          if (!confirmProjectSwitch()) return;
          // Persist before the document request so server bootstrap selects the new project.
          persistCurrentProjectId(projectId);
          reloadCurrentProjectPage(nextProject);
          return;
        }
        persistCurrentProjectId(projectId);
        // Store previous project ID for hooks that need to maintain state during transition
        previousProjectIdRef.current = activeCurrentProjectId;

        // Signal transition start
        setIsTransitioning(true);

        // Clear any existing transition timeout
        if (transitionTimeoutRef.current) {
          clearTimeout(transitionTimeoutRef.current);
        }

        // Update project ID
        setCurrentProjectId(projectId);

        // End transition after a brief delay (allows data hooks to fetch new data)
        // This prevents immediate "blink" where old data is cleared before new data arrives
        transitionTimeoutRef.current = setTimeout(() => {
          setIsTransitioning(false);
          previousProjectIdRef.current = null;
        }, 100);
      }
    },
    [activeProjects, activeCurrentProjectId]
  );

  // Cleanup transition timeout on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  const creationAttempts = useRef(new Map<string, string>());

  // Create a new project
  const createProject = useCallback(
    async (input: CreateProjectInput): Promise<Project> => {
      if (!user) {
        throw new Error('Not authenticated');
      }

      const token = await user.getIdToken();
      const digest = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(JSON.stringify(input))
      );
      const requestKey = `attune-project-create:${user.uid}:${Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('')}`;
      let attempt = input.idempotency_key || creationAttempts.current.get(requestKey);
      try {
        attempt ||= sessionStorage.getItem(requestKey) || undefined;
      } catch {
        /* Storage can be disabled. */
      }
      attempt ||= crypto.randomUUID();
      creationAttempts.current.set(requestKey, attempt);
      try {
        sessionStorage.setItem(requestKey, attempt);
      } catch {
        /* In-memory retries still work. */
      }
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...input, idempotency_key: attempt }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create project');
      }

      const newProject = await response.json();
      // Refresh projects list
      await fetchProjects(true);

      // Switch to new project
      setCurrentProjectId(newProject.id);
      persistCurrentProjectId(newProject.id);

      creationAttempts.current.delete(requestKey);
      try {
        sessionStorage.removeItem(requestKey);
      } catch {
        /* Storage can be disabled. */
      }

      return newProject;
    },
    [user, fetchProjects]
  );

  const createOnboardingProjectShell = useCallback(
    async (input?: CreateOnboardingProjectShellInput): Promise<Project> => {
      if (!user) {
        throw new Error('Not authenticated');
      }

      const token = await user.getIdToken();
      if (activeOwnerKeyRef.current !== user.uid)
        throw new Error('Your account changed. Try again.');
      const digest = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(JSON.stringify(input || {}))
      );
      const requestKey = `attune-onboarding-shell:${user.uid}:${Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('')}`;
      let attempt = input?.idempotency_key || creationAttempts.current.get(requestKey);
      try {
        attempt ||= sessionStorage.getItem(requestKey) || undefined;
      } catch {
        /* Storage can be disabled. */
      }
      attempt ||= crypto.randomUUID();
      creationAttempts.current.set(requestKey, attempt);
      try {
        sessionStorage.setItem(requestKey, attempt);
      } catch {
        /* In-memory retries still work. */
      }
      const response = await fetch('/api/projects/onboarding-shell', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...input, idempotency_key: attempt }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw Object.assign(
          new Error(errorData.error || 'Failed to create onboarding project shell'),
          { code: errorData.code }
        );
      }

      const newProject = (await response.json()) as Project;
      if (activeOwnerKeyRef.current !== user.uid)
        throw new Error('Your account changed. Try again.');
      creationAttempts.current.delete(requestKey);
      try {
        sessionStorage.removeItem(requestKey);
      } catch {
        /* Storage can be disabled. */
      }
      setProjects(current => [
        ...current.filter(project => project.id !== newProject.id),
        newProject,
      ]);
      setCurrentProjectId(newProject.id);
      persistCurrentProjectId(newProject.id);
      return newProject;
    },
    [user]
  );

  const completeProjectSetup = useCallback(
    async (projectId: string, status: 'completed' | 'skipped') => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      if (activeOwnerKeyRef.current !== user.uid)
        throw new Error('Your account changed. Try again.');
      const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/setup`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ setup_status: status }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Unable to open workspace');
      if (activeOwnerKeyRef.current !== user.uid)
        throw new Error('Your account changed. Try again.');
      setProjects(current =>
        current.map(project => (project.id === projectId ? payload.project : project))
      );
    },
    [user]
  );

  // Update a project
  const updateProject = useCallback(
    async (projectId: string, input: UpdateProjectInput): Promise<void> => {
      if (!user) {
        throw new Error('Not authenticated');
      }

      const token = await user.getIdToken();
      const response = await fetch(`/api/projects/${projectId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...input,
          ...(input.prompt_groups || input.analysis_settings?.prompts
            ? {
                expected_topic_revision: projectsRef.current.find(p => p.id === projectId)
                  ?.prompt_topics_revision,
                ...(input.prompt_groups
                  ? {
                      expected_topic_options_revision: projectsRef.current.find(
                        p => p.id === projectId
                      )?.topic_options_revision,
                    }
                  : {}),
              }
            : {}),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update project');
      }

      // Refresh projects list
      await fetchProjects(true);
    },
    [user, fetchProjects]
  );

  // Delete/archive a project
  const deleteProject = useCallback(
    async (
      projectId: string,
      force = false,
      options?: { selectFallback?: boolean }
    ): Promise<void> => {
      if (!user) {
        throw new Error('Not authenticated');
      }

      const token = await user.getIdToken();
      const url = force ? `/api/projects/${projectId}?force=true` : `/api/projects/${projectId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete project');
      }

      const deletingCurrentProject = currentProjectId === projectId;
      const clearProjectSelection = deletingCurrentProject && options?.selectFallback === false;
      if (clearProjectSelection) {
        setCurrentProjectId(null);
        persistCurrentProjectId(null);
        resetGroups();
      }

      // Refresh projects while allowing onboarding flows to intentionally keep selection empty.
      await fetchProjects(true, {
        clearProjectSelection,
      });

      // If deleted project was current, switch to another
      if (deletingCurrentProject && options?.selectFallback !== false) {
        const remaining = projects.filter(p => p.id !== projectId);
        if (remaining.length > 0) {
          setCurrentProjectId(remaining[0].id);
          persistCurrentProjectId(remaining[0].id);
        } else {
          setCurrentProjectId(null);
          persistCurrentProjectId(null);
        }
      }
    },
    [user, fetchProjects, currentProjectId, projects]
  );

  // Check migration status
  const checkMigrationStatus = useCallback(async (): Promise<MigrationStatus | null> => {
    if (!user) return null;

    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/projects/migrate', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch {
      return null;
    }
  }, [user]);

  // Migrate legacy brand_settings to project
  const migrateToProject = useCallback(async (): Promise<{
    success: boolean;
    project_id?: string;
  }> => {
    if (!user) {
      return { success: false };
    }

    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/projects/migrate', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        return { success: false };
      }

      const result = await response.json();

      if (result.success && result.project_id) {
        // Refresh projects and switch to new project
        await fetchProjects(true);
        setCurrentProjectId(result.project_id);
        persistCurrentProjectId(result.project_id);
      }

      return { success: result.success, project_id: result.project_id };
    } catch {
      return { success: false };
    }
  }, [user, fetchProjects]);

  // Fetch projects on mount and when user changes
  useEffect(() => {
    if (authLoading && !allowSessionCookieAuth) return;
    fetchProjects();
  }, [allowSessionCookieAuth, authLoading, fetchProjects]);

  // Hydrate projects from localStorage cache for faster initial render (per-user).
  useEffect(() => {
    if (authLoading || !user) return;
    if (
      hasInitialFetchRef.current ||
      (projectsOwnerKeyRef.current === user.uid && projectsRef.current.length > 0)
    )
      return;
    try {
      const cacheKey = `${PROJECTS_CACHE_PREFIX}${user.uid}`;
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as ProjectsCachePayload;
      if (!parsed?.fetchedAt || !Array.isArray(parsed.projects)) return;
      if (!Array.isArray(parsed.manageableWorkspaces)) return;
      if (typeof parsed.canCreatePersonalProject !== 'boolean') return;
      if (Date.now() - parsed.fetchedAt > PROJECTS_CACHE_TTL_MS) return;
      const cachedManageableWorkspaces = parsed.manageableWorkspaces;
      const cachedCanCreatePersonalProject = parsed.canCreatePersonalProject;
      if (
        parsed.projects.length === 0 &&
        cachedManageableWorkspaces.length === 0 &&
        !cachedCanCreatePersonalProject
      ) {
        return;
      }

      setProjects(parsed.projects);
      setManageableWorkspaces(cachedManageableWorkspaces);
      setCanCreatePersonalProject(cachedCanCreatePersonalProject);
      setProjectsOwnerKey(user.uid);
      const storedProjectId = getPersistedCurrentProjectId();

      if (storedProjectId && !parsed.projects.some(project => project.id === storedProjectId)) {
        return;
      }

      const validProjectId = selectValidProjectId(
        parsed.projects,
        storedProjectId,
        defaultProjectIdRef.current
      );
      setCurrentProjectId(validProjectId);
      persistCurrentProjectId(validProjectId);
      setLoading(false);
    } catch {
      // Non-blocking: ignore invalid cache
    }
  }, [authLoading, user]);

  // If default project id becomes available after initial load, reconcile current project id without refetching.
  useEffect(() => {
    if (authLoading || !user) return;
    // A UI cache may predate the selected project. Only a server-validated list
    // can justify replacing that selection with the account's default project.
    if (hydratedOwnerKey !== activeOwnerKey) return;
    if (activeProjects.length === 0) return;

    const storedProjectId = getPersistedCurrentProjectId();
    const nextId = selectValidProjectId(
      activeProjects,
      storedProjectId,
      userDocument?.default_project_id
    );
    if (nextId && nextId !== activeCurrentProjectId) {
      setCurrentProjectId(nextId);
      persistCurrentProjectId(nextId);
    }
  }, [
    authLoading,
    user,
    hydratedOwnerKey,
    activeOwnerKey,
    activeProjects,
    userDocument?.default_project_id,
    activeCurrentProjectId,
  ]);

  // Auto-migrate legacy users (brand_settings on user doc → project)
  // Runs once after initial fetch if user has no projects but has brand_settings
  useEffect(() => {
    async function autoMigrate() {
      // Skip if loading, no user, or user already has projects
      if (loading || isOwnerTransition || !user || activeProjects.length > 0) return;

      // Skip if user has no brand_settings (nothing to migrate)
      if (!userDocument?.brand_settings?.brand_name) return;

      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/projects/migrate', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.project_id) {
            // Refresh projects to pick up the new project
            await fetchProjects(true);
          }
        }
      } catch {
        // Non-blocking - user can still use the app
      }
    }

    autoMigrate();
  }, [
    loading,
    isOwnerTransition,
    user,
    userDocument?.brand_settings?.brand_name,
    activeProjects.length,
    fetchProjects,
  ]);

  const resolvedLoading =
    loading || isOwnerTransition || (activeProjects.length === 0 && managementPending);
  const resolvedReady =
    isReady &&
    !isOwnerTransition &&
    !(activeProjects.length === 0 && (managementPending || managementError));
  const managementStatus = useMemo(
    () => ({
      manageableWorkspacesLoading: managementPending && projectsBelongToActiveOwner,
      manageableWorkspacesError: projectsBelongToActiveOwner ? managementError : null,
    }),
    [managementPending, managementError, projectsBelongToActiveOwner]
  );

  const value = useMemo(
    () => ({
      currentProject,
      currentProjectId: activeCurrentProjectId,
      projects: activeProjects,
      manageableWorkspaces: activeManageableWorkspaces,
      canCreatePersonalProject: activeCanCreatePersonalProject,
      loading: resolvedLoading,
      projectsHydrated: Boolean(activeOwnerKey) && hydratedOwnerKey === activeOwnerKey,
      error,
      isReady: resolvedReady,
      isTransitioning,
      previousProjectId: previousProjectIdRef.current,
      fetchProjects,
      switchProject,
      createProject,
      createOnboardingProjectShell,
      completeProjectSetup,
      updateProject,
      deleteProject,
      checkMigrationStatus,
      migrateToProject,
    }),
    [
      currentProject,
      activeCurrentProjectId,
      activeProjects,
      activeManageableWorkspaces,
      activeCanCreatePersonalProject,
      resolvedLoading,
      activeOwnerKey,
      hydratedOwnerKey,
      error,
      resolvedReady,
      isTransitioning,
      fetchProjects,
      switchProject,
      createProject,
      createOnboardingProjectShell,
      completeProjectSetup,
      updateProject,
      deleteProject,
      checkMigrationStatus,
      migrateToProject,
    ]
  );

  return (
    <ProjectContext.Provider value={value}>
      <WorkspaceManagementStatusContext.Provider value={managementStatus}>
        <WorkspaceManagementSeedContext.Provider value={applyWorkspaceManagement}>
          {children}
        </WorkspaceManagementSeedContext.Provider>
      </WorkspaceManagementStatusContext.Provider>
    </ProjectContext.Provider>
  );
}
